import type { ProductCardProps } from '@hypercodestudio/basler-components';

import type { ProductsBySkusFragment } from '~~/lib/Shop/generated/schema';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import type { LightingAdvisorDecoration } from '~/utils/lightingAdvisor/catalogTypes';

type MagentoListProduct = NonNullable<
  NonNullable<ProductsBySkusFragment['items']>[number]
>;

function resolveLightingResultBadge(
  decorations: LightingAdvisorDecoration[],
  sku: string,
  textDictionary: Record<string, string | undefined>
): Pick<ProductCardProps, 'badgeText' | 'badgeStyle' | 'hasShineAnimation'> {
  const flags = decorations.filter(
    (entry) => entry.type === 'flag' && entry.ordernumber === sku
  );
  const bestFit = flags.find(
    (entry) =>
      entry.identifier === 'best_technical_fit' ||
      entry.identifier === 'best_fit'
  );
  if (bestFit) {
    return {
      badgeText:
        textDictionary['lightingAdvisor.decoration.best_technical_fit'] ??
        textDictionary['productsList.badge.bestTechnicalFit'] ??
        'Best Technical Fit',
      badgeStyle: 'primary',
      hasShineAnimation: true
    };
  }
  const ppr = flags.find(
    (entry) => entry.identifier === 'price_performance_ratio'
  );
  if (ppr) {
    return {
      badgeText:
        textDictionary['lightingAdvisor.decoration.price_performance_ratio'] ??
        textDictionary['productsList.badge.bestPricePerformance'] ??
        'Best Price/Performance',
      badgeStyle: 'primary',
      hasShineAnimation: true
    };
  }
  return {};
}

export function buildLightingAdvisorResultCardPropsList(
  skus: string[],
  products: MagentoListProduct[],
  options: {
    textDictionary: Record<string, string | undefined>;
    locale: string;
    createProductDetailLink: (product: DecoratedProduct) => string;
    decorations: LightingAdvisorDecoration[];
  }
): ProductCardProps[] {
  const productsBySku = new Map(
    products
      .filter((product) => product?.sku)
      .map((product) => [String(product.sku), product])
  );

  return skus.flatMap((sku) => {
    const product = productsBySku.get(sku);
    if (!product) {
      return [];
    }
    const decoratedProduct = DecoratedProduct.build(
      product,
      options.textDictionary,
      options.locale
    );
    const cardProps = decoratedProduct.asListEntry(
      {},
      options.createProductDetailLink,
      'Specifications',
      [],
      undefined
    );
    const badge = resolveLightingResultBadge(
      options.decorations,
      sku,
      options.textDictionary
    );
    return [
      {
        ...cardProps,
        ...badge,
        badgeText: badge.badgeText ?? cardProps.badgeText,
        cardDataAttributes: {
          'data-testid': `lighting-advisor-result-card-${sku}`
        }
      }
    ];
  });
}
